import { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { FetchContext } from 'context/FetchContext';

export const conversionError = (error) => {
  const message = error?.response?.data?.error || error?.response?.data?.message;
  return typeof message === 'string'
    ? message
    : 'Unable to complete the conversion request. Please try again.';
};

/**
 * One admin-center job feature: its status, the job being watched, starting a job and downloading the PDF report.
 * HTML conversion and Ask Eddy indexing behave the same way; only the address and the names differ.
 */
export function useWorkerJob({ base, key, fileName }) {
  const BASE = base;
  const { privateFetch } = useContext(FetchContext);
  const [job, setJob] = useState(null);
  const [conflict, setConflict] = useState(null);
  const status = useQuery(
    [`${key}-status`],
    () => privateFetch.get(`${BASE}/status`).then((res) => res.data),
    {
      retry: false,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchInterval: (data) => (data?.busy && !data?.running ? 3000 : false),
    }
  );

  useEffect(() => {
    if (status.data?.running?.jobId) setJob(status.data.running);
    if (status.data?.busy === false) setConflict(null);
  }, [status.data]);

  const jobQuery = useQuery(
    [`${key}-job`, job?.jobId],
    () => privateFetch.get(`${BASE}/jobs/${job.jobId}`).then((res) => res.data),
    {
      enabled: job?.state === 'running',
      retry: false,
      cacheTime: 0,
      refetchInterval: 3000,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setJob(data);
        if (data.state !== 'running') {
          setConflict(null);
          status.refetch();
        }
      },
    }
  );

  const start = useMutation(
    (body) => privateFetch.post(`${BASE}/jobs`, body).then((res) => res.data),
    {
      retry: false,
      onSuccess: (data) => {
        setConflict(null);
        setJob(data);
      },
      onError: (error) => {
        if (error?.response?.status === 409) {
          const running = error.response.data.running;
          setConflict(running || {});
          if (running?.jobId) setJob(running);
          status.refetch();
        }
      },
    }
  );

  const download = useMutation(async () => {
    const response = await privateFetch.get(`${BASE}/jobs/${job.jobId}/report`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${fileName}-${job.jobId}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });

  return { status, job, jobQuery, start, conflict, download };
}

export default function useHtmlConvert() {
  return useWorkerJob({
    base: '/v1/admin/html-convert',
    key: 'html-convert',
    fileName: 'html-conversion',
  });
}

export function useEddyIndex() {
  return useWorkerJob({
    base: '/v1/admin/eddy-index',
    key: 'eddy-index',
    fileName: 'ask-eddy-indexing',
  });
}
